import { useState } from 'react';
import styled from 'styled-components';
import { Input, Select } from '@shared/ui/Input';
import type { KadryRegistryRow } from '@entities/kadry';

/* Поиск и сортировка кандидатов: реестр заявки и страница «Подходит».
   Ищем на клиенте — строки уже загружены (реестр — до 700, «Подходит» —
   все ответившие). Запрос в адрес страницы не пишем: в нём ФИО и телефоны. */

export type KadryCandidateSort = 'answer' | 'fresh' | 'waiting' | 'score' | 'experience' | 'name';

export const KADRY_CANDIDATE_SORT_LABELS: Record<KadryCandidateSort, string> = {
  answer: 'Сначала ответившие «Подходит»',
  fresh: 'Свежие ответы сверху',
  waiting: 'Сначала ждущие звонка',
  score: 'По баллу',
  experience: 'По опыту в роли',
  name: 'По ФИО',
};

const norm = (s: string) => s.toLowerCase().replace(/ё/g, 'е');
const digits = (s: string) => s.replace(/\D/g, '');

/** Номер без кода страны: «+7 922…», «8 922…» и «922…» ищутся одинаково. */
function phoneCore(d: string): string {
  return /^[78]9/.test(d) ? d.slice(1) : d;
}

/** Запрос похож на номер телефона (цифры, пробелы, «+», скобки, дефисы). */
const PHONE_QUERY = /^[\s+\d()-]+$/;

/**
 * Подходит ли строка под запрос: каждое слово запроса должно найтись в ФИО,
 * должности, почте, комментариях коллег или в том, кто связался; слово из
 * цифр (от 3) — ещё и в телефоне. `extra` — что ещё ищется (название вакансии).
 */
export function kadryCandidateMatches(row: KadryRegistryRow, query: string, extra: string[] = []): boolean {
  const words = norm(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const phones = row.phones.map((p) => phoneCore(digits(p)));
  // «8 922 470-55» — номер целиком, а не три отдельных слова.
  if (PHONE_QUERY.test(query.trim()) && digits(query).length >= 3) {
    const q = phoneCore(digits(query));
    return phones.some((p) => p.includes(q));
  }
  const text = norm(
    [
      row.full_name,
      row.headline,
      ...row.emails,
      ...row.phones,
      row.outreach?.hr_contacted_by,
      ...row.comments.flatMap((c) => [c.text, c.author_name]),
      ...extra,
    ]
      .filter(Boolean)
      .join(' \n'),
  );
  return words.every((w) => {
    if (text.includes(w)) return true;
    const d = digits(w);
    if (d.length < 3 || !/^[+\d()\-]+$/.test(w)) return false;
    const q = phoneCore(d);
    return phones.some((p) => p.includes(q));
  });
}

const respondedAt = (r: KadryRegistryRow) =>
  r.outreach?.responded_at ? Date.parse(r.outreach.responded_at) : 0;
const byYears = (a: KadryRegistryRow, b: KadryRegistryRow) =>
  (b.experience_years ?? -1) - (a.experience_years ?? -1);
/** При равном балле — больше стажа; дальше порядок сервера (свежее резюме выше). */
const byScore = (a: KadryRegistryRow, b: KadryRegistryRow) => b.match.score - a.match.score || byYears(a, b);

/** «Подходит» → ждём ответа → без предложения → «Не подходит». */
function answerRank(r: KadryRegistryRow): number {
  const o = r.outreach;
  if (!o) return 2;
  if (o.response === 'interested') return 0;
  if (o.response == null) return 1;
  return 3;
}

/** Пишут «не связались» → ждут звонка → связались → прочие. */
function callRank(r: KadryRegistryRow): number {
  const o = r.outreach;
  if (!o || o.response !== 'interested') return 3;
  if (o.hr_not_contacted) return 0;
  if (!o.hr_contacted_at) return 1;
  return 2;
}

const byName = (a: KadryRegistryRow, b: KadryRegistryRow) => {
  if (!a.full_name || !b.full_name) return (a.full_name ? 0 : 1) - (b.full_name ? 0 : 1);
  return a.full_name.localeCompare(b.full_name, 'ru');
};

export function kadrySortCandidates(rows: KadryRegistryRow[], key: KadryCandidateSort): KadryRegistryRow[] {
  const out = [...rows];
  switch (key) {
    case 'answer':
      out.sort((a, b) => answerRank(a) - answerRank(b) || respondedAt(b) - respondedAt(a) || byScore(a, b));
      break;
    case 'fresh':
      out.sort((a, b) => respondedAt(b) - respondedAt(a) || byScore(a, b));
      break;
    case 'waiting':
      // Внутри — кто ждёт дольше, тот выше.
      out.sort((a, b) => callRank(a) - callRank(b) || respondedAt(a) - respondedAt(b));
      break;
    case 'experience':
      out.sort((a, b) => byYears(a, b) || byScore(a, b));
      break;
    case 'name':
      out.sort((a, b) => byName(a, b) || byScore(a, b));
      break;
    default:
      out.sort(byScore);
  }
  return out;
}

/** Сортировка, запомненная в браузере (у каждой страницы своя). */
export function useKadryCandidateSort(
  storageKey: string,
  options: readonly KadryCandidateSort[],
  fallback: KadryCandidateSort,
): [KadryCandidateSort, (s: KadryCandidateSort) => void] {
  const key = `osnova.kadry.sort.${storageKey}`;
  const [sort, setSort] = useState<KadryCandidateSort>(() => {
    try {
      const v = localStorage.getItem(key) as KadryCandidateSort | null;
      return v && options.includes(v) ? v : fallback;
    } catch {
      return fallback;
    }
  });
  const set = (s: KadryCandidateSort) => {
    setSort(s);
    try {
      localStorage.setItem(key, s);
    } catch {
      /* без запоминания */
    }
  };
  return [sort, set];
}

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  input {
    flex: 1 1 260px;
    min-width: 0;
  }
  select {
    width: auto;
    min-width: 230px;
  }
`;

const Found = styled.span`
  white-space: nowrap;
`;

/** Строка поиска и выбор сортировки над списком кандидатов. */
export function KadryCandidateFinder({
  query,
  onQuery,
  sort,
  onSort,
  options,
  found,
  total,
  placeholder = 'Поиск: ФИО, телефон, должность, почта, комментарий',
}: {
  query: string;
  onQuery: (q: string) => void;
  sort: KadryCandidateSort;
  onSort: (s: KadryCandidateSort) => void;
  options: readonly KadryCandidateSort[];
  found: number;
  total: number;
  placeholder?: string;
}) {
  return (
    <Bar>
      <Input
        type="search"
        value={query}
        placeholder={placeholder}
        aria-label="Поиск кандидата"
        onChange={(e) => onQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onQuery('');
        }}
      />
      <Select
        value={sort}
        aria-label="Сортировка кандидатов"
        onChange={(e) => onSort(e.target.value as KadryCandidateSort)}
      >
        {options.map((k) => (
          <option key={k} value={k}>
            {KADRY_CANDIDATE_SORT_LABELS[k]}
          </option>
        ))}
      </Select>
      {query.trim() && (
        <Found aria-live="polite">
          найдено {found} из {total}
        </Found>
      )}
    </Bar>
  );
}
